import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type JSX,
} from 'react';
import { Outlet, ScrollRestoration, useLocation } from 'react-router-dom';
import { SiteBody, SiteHeader, CopyrightFooter, LayoutMetricsContext } from '@/components/layout';
import { Toaster } from 'sonner';

export function AppLayout(): JSX.Element {
  const location = useLocation();
  const isDashboardRoute = location.pathname.startsWith('/dashboard');

  const headerRef = useRef<HTMLElement | null>(null);
  const footerRef = useRef<HTMLElement | null>(null);
  const [layoutVars, setLayoutVars] = useState<Record<string, string>>({
    '--layout-safe-viewport': '100vh',
    '--layout-header-height': '0px',
    '--layout-footer-height': '0px',
    '--layout-dashboard-topbar': '0px',
    '--dashboard-sticky-top': 'calc(env(safe-area-inset-top, 0px) + 0.75rem)',
    '--layout-shell-sidebar': '18rem',
    '--layout-shell-sidebar-collapsed': '4.5rem',
  });

  const updateVar = useCallback((variable: string, value: string) => {
    setLayoutVars((previous) => {
      if (previous[variable] === value) {
        return previous;
      }
      return { ...previous, [variable]: value };
    });
  }, []);

  const toPx = useCallback((value: number) => `${String(Math.round(value))}px`, []);

  useEffect(() => {
    const updateViewport = () => {
      const viewportHeight = window.visualViewport?.height ?? window.innerHeight;
      updateVar('--layout-safe-viewport', toPx(viewportHeight));
    };

    updateViewport();

    window.addEventListener('resize', updateViewport);
    window.visualViewport?.addEventListener('resize', updateViewport);

    return () => {
      window.removeEventListener('resize', updateViewport);
      window.visualViewport?.removeEventListener('resize', updateViewport);
    };
  }, [toPx, updateVar]);

  useLayoutEffect(() => {
    const headerElement = headerRef.current;

    if (!headerElement) {
      updateVar('--layout-header-height', '0px');
      return;
    }

    const setHeight = () => {
      const height = headerElement.getBoundingClientRect().height;
      updateVar('--layout-header-height', toPx(height));
    };

    setHeight();

    if (typeof ResizeObserver === 'undefined') {
      return;
    }

    const observer = new ResizeObserver(setHeight);
    observer.observe(headerElement);

    return () => {
      observer.disconnect();
    };
  }, [isDashboardRoute, toPx, updateVar]);

  useLayoutEffect(() => {
    const footerElement = footerRef.current;

    if (!footerElement) {
      updateVar('--layout-footer-height', '0px');
      return;
    }

    const setHeight = () => {
      const height = footerElement.getBoundingClientRect().height;
      updateVar('--layout-footer-height', toPx(height));
    };

    setHeight();

    if (typeof ResizeObserver === 'undefined') {
      return;
    }

    const observer = new ResizeObserver(setHeight);
    observer.observe(footerElement);

    return () => {
      observer.disconnect();
    };
  }, [toPx, updateVar]);

  const layoutContextValue = useMemo(
    () => ({
      reportTopbarHeight: (height: number) => {
        updateVar('--layout-dashboard-topbar', toPx(height));
      },
    }),
    [toPx, updateVar],
  );

  return (
    <LayoutMetricsContext.Provider value={layoutContextValue}>
      <div
        className="flex min-h-[100dvh] min-h-[100svh] flex-col bg-surface-light text-text-primary"
        data-layout-scope={isDashboardRoute ? 'dashboard' : 'public'}
        style={layoutVars}
      >
        {isDashboardRoute ? (
          <>
            <SiteBody scrollable={false}>
              <Outlet />
            </SiteBody>
            <CopyrightFooter ref={footerRef} variant="compact" />
          </>
        ) : (
          <>
            <SiteHeader ref={headerRef} />
            <SiteBody id="main-content" scrollable subtractFooter className={undefined}>
              <Outlet />
            </SiteBody>
            <CopyrightFooter ref={footerRef} variant="default" className={undefined} />
          </>
        )}
        <ScrollRestoration />
        <Toaster position="top-center" richColors closeButton />
      </div>
    </LayoutMetricsContext.Provider>
  );
}
